import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import type { Task } from "../sync/types";
import { TaskCard } from "./TaskCard";
import { Badge, Card, EmptyState, Page } from "./ui";
import { t, tn } from "../i18n";

// TasksView lists the tasks the user asked for, is assigned, or worked on.
export function TasksView() {
  const session = useSession();
  const state = useLantern();
  const [ids, setIds] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void session.api.myTasks().then(
      ({ tasks }: { tasks: Task[] }) => {
        if (!live) return;
        session.store.update((s) => tasks.forEach((task) => s.tasks.set(task.id, task)));
        setIds(tasks.map((task) => task.id));
      },
      () => live && setIds([]),
    );
    return () => {
      live = false;
    };
  }, [session, state.taskNotices]);

  const me = state.me?.id;
  const all = (ids ?? []).map((id) => state.tasks.get(id)).filter((task): task is Task => !!task);
  const forMe = all.filter((task) => task.assignee === me && !["completed", "failed", "cancelled", "dead_lettered"].includes(task.state));
  const others = all.filter((task) => !forMe.includes(task));
  // Approvals asking the same thing are decided together (architecture
  // doc, human oversight).
  const groups = new Map<string, Task[]>();
  for (const task of forMe) {
    if (task.capability !== "lantern.approval/v1" || task.state !== "queued") continue;
    const prompt = (task.input as { prompt?: string } | null)?.prompt ?? "";
    groups.set(prompt, [...(groups.get(prompt) ?? []), task]);
  }
  const grouped = new Set([...groups.values()].filter((g) => g.length > 1).flat());

  async function decideAll(list: Task[], decision: "approve" | "reject") {
    setBusy(true);
    try {
      for (const task of list) {
        const { lease } = await session.api.acceptTask(task.id);
        await session.api.completeTask(task.id, lease.epoch, { decision });
      }
    } finally {
      setBusy(false);
      session.store.update((s) => s.taskNotices++);
    }
  }
  const approvals = [...groups.entries()].filter(([, g]) => g.length > 1);
  const mineLeft = forMe.filter((task) => !grouped.has(task));

  return (
    <Page className="tasks-view" title={t("Tasks")} description={t("Work handed to you by people, agents and workflows, and what you asked others for.")}>
      {ids === null && <p className="muted">{t("Loading…")}</p>}
      {ids?.length === 0 && (
        <Card>
          <EmptyState icon="🗂️" title={t("No tasks yet")}>
            {t("Tasks people and agents ask of you, and ones you ask of them, show up here.")}
          </EmptyState>
        </Card>
      )}
      {(approvals.length > 0 || mineLeft.length > 0) && (
        <Card title={t("For you")} description={t("Waiting on you to accept, decide or complete.")} actions={<Badge tone="warn">{t("{n} open", { n: forMe.length })}</Badge>}>
          <div className="task-cards">
            {approvals.map(([prompt, g]) => (
              <div key={prompt} className="approval-group" aria-label={t("Approvals")}>
                <header>
                  <Badge tone="accent">{tn(g.length, "{n} approval", "{n} approvals")}</Badge> <strong>{prompt}</strong>
                </header>
                <div className="task-actions">
                  <button className="primary" disabled={busy} onClick={() => decideAll(g, "approve")}>
                    {t("Approve all {n}", { n: g.length })}
                  </button>
                  <button className="secondary" disabled={busy} onClick={() => decideAll(g, "reject")}>
                    {t("Reject all")}
                  </button>
                </div>
              </div>
            ))}
            {mineLeft.map((task) => (
              <TaskCard key={task.id} id={task.id} />
            ))}
          </div>
        </Card>
      )}
      {others.length > 0 && (
        <Card title={t("Recent")} description={t("Tasks you asked for, or finished.")}>
          <div className="task-cards">
            {others.map((task) => (
              <TaskCard key={task.id} id={task.id} />
            ))}
          </div>
        </Card>
      )}
    </Page>
  );
}
